import get from "lodash/get";
import { FunctionComponent, ReactNode, useEffect, useMemo } from "react";

import themeVars from "@voxmedia/duet-ui/themes/theme-contract.css";
import { type CommunityThemeConfig } from "@voxmedia/duet-ui/utils/theme.utils";

interface Props {
  communityThemeConfig?: CommunityThemeConfig;
  children?: ReactNode;
}

// Allow for tokens from the brand's theme.config.ts to override the default theme
const CommunityTheme: FunctionComponent<Props> = ({
  communityThemeConfig,
  children,
}) => {
  const communityTheme: Record<string, string> | null = useMemo(() => {
    const theme: Record<string, string> = {};

    if (!communityThemeConfig) {
      return null;
    }

    Object.entries(communityThemeConfig.overrides).forEach(
      ([tokenName, value]) => {
        // Override the system token
        const themeVar = get(themeVars, tokenName) as string;
        if (themeVar) {
          // themeVar looks like var(--sys-color-background__1glk8s8), remove the var() part
          const propertyName = themeVar.replace(/var\(([^)]+)\)/, "$1");
          theme[propertyName] = value;

          // And add coral-ified version of system tokens so coral can use it
          const coralVariableName = `--coral${propertyName.replace(/__\w+$/, "").replace(/^--/, "-")}`;
          theme[coralVariableName] = value;
        }

        // Determine any comp tokens that alias this system token and override them too
        const compTokens =
          communityThemeConfig.componentTokens[tokenName] || [];
        compTokens.forEach((compToken: string) => {
          const cleanedTokenName = compToken.replace(/\.value.*/, "");
          const compThemeVar = get(themeVars, cleanedTokenName) as string;

          if (!compThemeVar) {
            return;
          }

          // themeVar looks like var(--comp-foo-bar__1glk8s8), remove the var() part
          let variableName = compThemeVar.replace(/var\(([^)]+)\)/, "$1");

          // If the token is a coral comp token, it needs to be further sanitized
          if (variableName.startsWith("--comp-coral")) {
            // Remove the hash suffix (e.g. __1glk8s8) and prefix with "coral-"
            variableName = `--coral-${variableName.slice(2).replace(/__\w+$/, "")}`;
          }

          // Replace the hex code in the value with the override value
          const hexRegex = /#([0-9A-Fa-f]{3,6})/g;
          const tokenValue = get(
            communityThemeConfig.themeData,
            cleanedTokenName,
          ) as string;

          if (tokenValue) {
            // Need to check if the token value has a hex code with an alpha channel (e.g. #0000ff66)
            theme[variableName] = tokenValue.replace(hexRegex, value);
          }
        });
      },
    );

    return theme;
  }, [communityThemeConfig]);

  useEffect(() => {
    // Apply theme styles to the body tag so that elements outside
    //  of the main next container can access them (i.e. coral modals)
    if (communityTheme && Object.keys(communityTheme).length > 0) {
      Object.entries(communityTheme).forEach(([key, value]) => {
        document.body.style.setProperty(key, value);
      });
    }
  }, [communityTheme]);

  return communityTheme && Object.keys(communityTheme).length > 0 ? (
    <div style={communityTheme}>{children}</div>
  ) : (
    children
  );
};

export default CommunityTheme;
